/** Captain app progress labels (v2 summary contract). Fallback when dashboard-config has no list. */
export const DEFAULT_SLOT_TITLE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Full-Day', value: 'Full-Day' },
  { label: 'Early Morning', value: 'Early Morning' },
  { label: 'Afternoon', value: 'Afternoon' },
  { label: 'Evening', value: 'Evening' },
  { label: 'Late Night', value: 'Late Night' },
];

/** @deprecated use DEFAULT_SLOT_TITLE_OPTIONS */
export const SLOT_TITLE_OPTIONS = DEFAULT_SLOT_TITLE_OPTIONS;

export type SlotTitleOption = { label: string; value: string };

type SlotTitleConfigEntry = string | { label?: string; value?: string };

function parseSlotTitleList(raw: unknown): SlotTitleOption[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const options: SlotTitleOption[] = [];
  for (const entry of raw as SlotTitleConfigEntry[]) {
    if (typeof entry === 'string') {
      const value = entry.trim();
      if (value) {
        options.push({ label: value, value });
      }
      continue;
    }
    if (entry && typeof entry === 'object') {
      const value = entry.value != null ? String(entry.value).trim() : '';
      if (!value) {
        continue;
      }
      const label = entry.label != null ? String(entry.label).trim() : value;
      options.push({ label: label || value, value });
    }
  }
  return options;
}

function mergeUniqueSlotTitleOptions(lists: SlotTitleOption[][]): SlotTitleOption[] {
  const seen = new Set<string>();
  const merged: SlotTitleOption[] = [];
  for (const list of lists) {
    for (const opt of list) {
      if (!seen.has(opt.value)) {
        seen.add(opt.value);
        merged.push(opt);
      }
    }
  }
  return merged;
}

function defaultSlotTitleFallback(): SlotTitleOption[] {
  return DEFAULT_SLOT_TITLE_OPTIONS.slice();
}

function byServiceIdMapFromConfig(dashboardConfig: unknown): Record<string, SlotTitleConfigEntry[]> {
  if (!dashboardConfig || typeof dashboardConfig !== 'object') {
    return {};
  }
  const raw = (dashboardConfig as Record<string, unknown>).incentiveSlotTitlesByServiceId;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw as Record<string, SlotTitleConfigEntry[]>;
}

function slotTitleListForService(
  byServiceId: Record<string, SlotTitleConfigEntry[]>,
  serviceId: string,
): SlotTitleConfigEntry[] | undefined {
  if (byServiceId[serviceId]) {
    return byServiceId[serviceId];
  }
  const target = serviceId.toLowerCase();
  for (const key of Object.keys(byServiceId)) {
    if (key.toLowerCase() === target) {
      return byServiceId[key];
    }
  }
  return undefined;
}

/**
 * Resolves slot title dropdown options from dashboard-config.
 *
 * - `incentiveSlotTitlesByServiceId` — keyed by global `service._id` (`basicInfo.serviceIdsForConfig[]`)
 *
 * One selected service: that service's list, else in-code defaults.
 * Multiple services: union of each service's list (override or defaults per service), deduped by value.
 */
export function resolveIncentiveSlotTitleOptions(
  dashboardConfig: unknown,
  serviceIds: string[] = [],
): SlotTitleOption[] {
  const fallback = defaultSlotTitleFallback();
  const byServiceId = byServiceIdMapFromConfig(dashboardConfig);
  const ids = (serviceIds || []).map(id => String(id).trim()).filter(Boolean);
  if (ids.length === 0) {
    return fallback;
  }

  const lists: SlotTitleOption[][] = [];
  for (const id of ids) {
    const overrideRaw = slotTitleListForService(byServiceId, id);
    const parsed = parseSlotTitleList(overrideRaw);
    lists.push(parsed.length > 0 ? parsed : fallback);
  }
  if (lists.length === 1) {
    return lists[0];
  }
  const merged = mergeUniqueSlotTitleOptions(lists);
  return merged.length > 0 ? merged : fallback;
}

/**
 * Global list only (no per-service resolution).
 * @deprecated prefer resolveIncentiveSlotTitleOptions
 */
export function slotTitleOptionsFromDashboardConfig(
  dashboardConfig: unknown,
): SlotTitleOption[] {
  return resolveIncentiveSlotTitleOptions(dashboardConfig, []);
}
